import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, { JSX } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom'

import { AppLayout } from 'components/app-shell/AppLayout'
import { AdminRoute } from 'components/auth/AdminRoute'
import { ProtectedRoute } from 'components/auth/ProtectedRoute'
import { Provider } from 'components/ui/provider'
import { AppToaster } from 'components/ui/toaster'
import { QUERY_RETRY_COUNT, QUERY_STALE_TIME_MS } from 'config/queryPolicy'
import { APP_ROUTES } from 'config/routes'
import { AuthProvider } from 'contexts/AuthContext'
import 'App.css'

/**
 * Layout wrapper component that renders the AppLayout with children
 *
 * ALL routes are rendered as children of this Layout, ensuring
 * that the AppLayout (header + breadcrumbs) is always applied.
 * It is not possible to create a route without this wrapper.
 *
 * Wrapper for routes that should always render inside the shared app shell.
 */
const Layout = () => {
  /* --- Section 6: Render --- */
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  )
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_STALE_TIME_MS,
      retry: QUERY_RETRY_COUNT,
    },
  },
})

const getRouteElement = (route: (typeof APP_ROUTES)[number]) => {
  const PageComponent = route.component

  if (route.authGuard === 'required') {
    return (
      <ProtectedRoute>
        <PageComponent />
      </ProtectedRoute>
    )
  }

  if (route.authGuard === 'admin') {
    return (
      <AdminRoute>
        <PageComponent />
      </AdminRoute>
    )
  }

  return <PageComponent />
}

export const App = (): JSX.Element => {
  /* --- Section 6: Render --- */
  return (
    <QueryClientProvider client={queryClient}>
      <Provider>
        <AuthProvider>
          <Router>
            <Routes>
              <Route element={<Layout />}>
                {/* Redirect root to /new */}
                <Route path="/" element={<Navigate to="/new" replace />} />

                {/* All app routes are defined in config/routes.ts and automatically include AppLayout */}
                {APP_ROUTES.map(route => {
                  return <Route key={route.path} path={route.path} element={getRouteElement(route)} />
                })}
              </Route>
            </Routes>
          </Router>
          <AppToaster />
        </AuthProvider>
      </Provider>
    </QueryClientProvider>
  )
}
